/**
 * ARAM Platform - Sidebar Navigation
 * Core Desarrollos S.A.S - Copyright 2024-2025
 */

import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { getDefaultModeForTenant, isCoreRouteVisibleForMode, isLogisticsChildVisibleForMode, isRouteEnabled, isSectionVisibleForMode, type VerticalKey } from '@/config/verticals'
import { useLogisticsModeStore } from '@/stores/logisticsModeStore'
import { useDemoNavigationStore, type DemoSectionKey } from '@/stores/demoNavigationStore'
import {
  Truck,
  Package,
  Route,
  MapPin,
  Cpu,
  FileText,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  X,
  Landmark,
  Wallet,
  Sprout,
  Plane,
  Mountain,
  FilePen,
  ScanLine,
  Users,
  FolderKanban,
  Calculator,
  Boxes,
  Store,
  Factory,
} from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { matchesRoutePermission } from '@/components/navigation/sidebarPermissions'

interface SidebarProps {
  open: boolean
  onClose: () => void
}

interface NavChild {
  to: string
  label: string
  permission?: string
  group?: 'Operación' | 'Gestión' | 'General'
  legacyPaths?: string[]
}

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  collapsed: boolean
  permission?: string
  module?: string
  children?: NavChild[]
}

function NavItem({ to, icon, label, collapsed, permission, module, children }: NavItemProps) {
  const location = useLocation()
  const hasActiveChild = children?.some((child) =>
    location.pathname === child.to || (child.to !== '/distribution' && location.pathname.startsWith(`${child.to}/`))
  ) ?? false
  const [expanded, setExpanded] = useState(hasActiveChild)
  const { hasPermission, user } = useAuthStore()
  const enabledModules = user?.enabled_modules ?? []
  const menuPerms = user?.menu_permissions  // null|undefined = use vertical defaults; string[] = explicit list

  useEffect(() => {
    if (hasActiveChild) setExpanded(true)
  }, [hasActiveChild])

  // ── menu_permissions override ──────────────────────────────────
  // When a per-user list is set, it fully replaces module/permission checks for UI visibility.
  if (!user?.is_superuser && menuPerms !== null && menuPerms !== undefined) {
    const allAllowed = menuPerms.includes('ALL')

    if (children) {
      const visibleChildren = allAllowed
        ? children
        : children.filter((child) => {
            const parentAllowed = matchesRoutePermission(to, menuPerms, allAllowed)
            if (parentAllowed) return true
            return [child.to, ...(child.legacyPaths ?? [])].some((route) =>
              matchesRoutePermission(route, menuPerms, allAllowed)
            )
          })

      if (!allAllowed && visibleChildren.length === 0) return null

      return (
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors',
              collapsed && 'justify-center'
            )}
          >
            {icon}
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{label}</span>
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </>
            )}
          </button>
          {expanded && !collapsed && (
            <div className="ml-8 mt-1 space-y-1">
              {visibleChildren.map((child, index) => (
                <Fragment key={child.to}>
                  {child.group && child.group !== visibleChildren[index - 1]?.group && <p className="px-3 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/55">{child.group}</p>}
                <NavLink
                  to={child.to}
                  end
                  className={({ isActive }) =>
                    cn(
                      'block px-3 py-2 rounded-md text-sm transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )
                  }
                >
                  {child.label}
                </NavLink>
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )
    }

    // Single item
    if (!allAllowed && !matchesRoutePermission(to, menuPerms, allAllowed)) return null

    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
            isActive
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            collapsed && "justify-center"
          )
        }
      >
        {icon}
        {!collapsed && <span>{label}</span>}
      </NavLink>
    )
  }

  // ── Default: vertical/role-based checks ───────────────────────

  // Check permission gate
  if (permission && !hasPermission(permission)) {
    return null
  }

  // Check vertical module gate (if module is specified)
  if (
    module &&
    enabledModules.length > 0 &&
    !enabledModules.includes('_full') &&
    !enabledModules.includes(module)
  ) {
    return null
  }

  // For items without explicit module, check route-based mapping
  if (!module && enabledModules.length > 0 && !isRouteEnabled(to, enabledModules)) {
    return null
  }

  if (children) {
    // Filter children by permission AND by vertical module
    const visibleChildren = children
      .filter((child) => !child.permission || hasPermission(child.permission))
      .filter((child) => enabledModules.length === 0 || isRouteEnabled(child.to, enabledModules))
    
    if (visibleChildren.length === 0) return null

    return (
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            "flex items-center gap-3 w-full px-3 py-2 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors",
            collapsed && "justify-center"
          )}
        >
          {icon}
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{label}</span>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </>
          )}
        </button>
        
        {expanded && !collapsed && (
          <div className="ml-8 mt-1 space-y-1">
            {visibleChildren.map((child, index) => (
              <Fragment key={child.to}>
                {child.group && child.group !== visibleChildren[index - 1]?.group && <p className="px-3 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/55">{child.group}</p>}
              <NavLink
                to={child.to}
                end
                className={({ isActive }) =>
                  cn(
                    "block px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )
                }
              >
                {child.label}
              </NavLink>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          collapsed && "justify-center"
        )
      }
    >
      {icon}
      {!collapsed && <span>{label}</span>}
    </NavLink>
  )
}

function SectionHeader({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (collapsed) return <div className="my-1.5 border-t border-border/40" />
  return (
    <div className="pt-4 pb-1 px-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 select-none">{label}</p>
    </div>
  )
}

type NavEntry = NavItemProps | { type: 'section'; label: string; modules?: string[] }

const DEMO_SECTION_BY_LABEL: Partial<Record<string, DemoSectionKey>> = {
  Logística: 'logistics',
  Organización: 'organization',
  Finanzas: 'finance',
  Tecnología: 'technology',
  'Business Intelligence': 'business_intelligence',
}
const EMPTY_DEMO_SECTIONS: DemoSectionKey[] = []

function filterSuperuserNavigation(
  items: NavEntry[],
  mode: VerticalKey,
  selectedSections: DemoSectionKey[]
): NavEntry[] {
  let sectionLabel = ''
  return items.filter((item) => {
    if ('type' in item) {
      sectionLabel = item.label
      return true
    }
    if (mode === 'admin') return true
    if (sectionLabel === 'Verticales') return isCoreRouteVisibleForMode(item.to, mode)
    const section = DEMO_SECTION_BY_LABEL[sectionLabel]
    return section ? selectedSections.includes(section) : false
  })
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const collapsed = !open
  const { user } = useAuthStore()
  const { activeMode } = useLogisticsModeStore()
  const selectedDemoSections = useDemoNavigationStore(
    (state) => state.sectionsByVertical[activeMode] ?? EMPTY_DEMO_SECTIONS
  )
  const effectiveMode = user?.is_superuser
    ? activeMode
    : getDefaultModeForTenant(user?.vertical)

  const allNavItems: NavEntry[] = [
    // ─── VERTICALES ───────────────────────────────────────────────
    // Portales por industria — visibles según la vertical activa del tenant
    { type: 'section', label: 'Verticales', modules: ['freight', 'distribution', 'industrial', 'agroalimentario', 'procesadoras', 'actas_mineria', 'traceability', 'rental'] },
    {
      to: '/distribution',
      icon: <Store className="h-5 w-5" />,
      label: 'ARAM Distribution',
      collapsed,
      module: 'distribution',
      children: [
        { to: '/distribution', label: 'Dashboard', group: 'Operación', legacyPaths: ['/distribution/exceptions'] },
        { to: '/distribution/orders', label: 'Pedidos', group: 'Operación' },
        { to: '/distribution/stock', label: 'Stock e Inventario', group: 'Operación', legacyPaths: ['/distribution/warehouses', '/distribution/acopio', '/last-mile'] },
        { to: '/distribution/routes', label: 'Rutas y Repartos', group: 'Operación', legacyPaths: ['/last-mile'] },
        { to: '/distribution/deliveries', label: 'Entregas', group: 'Operación', legacyPaths: ['/distribution/returns', '/distribution/exceptions', '/last-mile'] },
        { to: '/distribution/customers', label: 'Clientes', group: 'Gestión' },
        { to: '/distribution/catalog', label: 'Catálogo y Precios', group: 'Gestión' },
        { to: '/distribution/administration', label: 'Administración', group: 'Gestión', legacyPaths: ['/distribution/collections', '/distribution/procurement', '/distribution/fiscal-operations'] },
        { to: '/distribution/reports', label: 'Reportes', group: 'General' },
        { to: '/distribution/configuration', label: 'Configuración', group: 'General' },
      ],
    },
    {
      to: '/industrial',
      icon: <Factory className="h-5 w-5" />,
      label: 'Industria Inteligente',
      collapsed,
      module: 'industrial',
      children: [
        { to: '/industrial', label: 'Centro Industrial' },
        { to: '/industrial/warehouse', label: 'Smart Warehouse' },
        { to: '/industrial/warehouse/counts', label: 'Conteos y Conciliación', permission: 'distribution.stock.handheld' },
        { to: '/industrial/logistics', label: 'Logística Conectada' },
        { to: '/industrial/assets', label: 'Asset Intelligence' },
      ],
    },
    // FORWARDER
    {
      to: '/freight',
      icon: <Package className="h-5 w-5" />,
      label: 'Forwarder',
      collapsed,
      permission: 'orders:read',
      children: [
        { to: '/freight', label: 'Freight Forwarding' },
        { to: '/freight/import', label: '  · Import Operations' },
        { to: '/freight/export', label: '  · Export Operations' },
        { to: '/freight/mro', label: '  · Critical MRO' },
        { to: '/freight/global', label: 'Multimodal & Global' },
        { to: '/freight/ncm-classifier', label: 'Clasificador NCM' },
        { to: '/freight/route-editor', label: 'Editor de Rutas' },
      ],
    },
    // AGRO
    {
      to: '/fincas',
      icon: <Sprout className="h-5 w-5" />,
      label: 'Agro',
      collapsed,
      children: [
        { to: '/fincas', label: 'Fincas & Campo' },
        { to: '/fincas/parcelas', label: '  · Parcelas' },
        { to: '/fincas/cultivos', label: '  · Cultivos' },
        { to: '/fincas/sensores', label: '  · Sensores IoT' },
        { to: '/fincas/alertas', label: '  · Alertas & Eventos' },
        { to: '/fincas/activos', label: '  · Activos' },
        { to: '/fincas/trazabilidad', label: '  · Trazabilidad' },
        { to: '/fincas/cuaderno', label: '  · Cuaderno de Campo' },
        { to: '/fincas/meteorologia', label: '  · Meteorología' },
        { to: '/fincas/satelital', label: '  · Agro Satelital' },
      ],
    },
    // PROCESADORAS & MANUFACTURA
    {
      to: '/procesadora',
      icon: <Factory className="h-5 w-5" />,
      label: 'Procesadoras & Manufactura',
      collapsed,
      children: [
        { to: '/procesadora', label: 'Panel de Procesamiento' },
        { to: '/procesadora/recepcion', label: '  · Recepción' },
        { to: '/procesadora/lineas', label: '  · Líneas de Proceso' },
        { to: '/procesadora/empaque', label: '  · Empaque' },
        { to: '/procesadora/almacen', label: '  · Cámaras Frío' },
        { to: '/procesadora/despacho', label: '  · Despacho' },
        { to: '/procesadora/trazabilidad', label: '  · Trazabilidad' },
        { to: '/procesadora/calidad', label: '  · Control Calidad' },
      ],
    },
    // MINERÍA (vertical: energía, oil & gas, minería)
    {
      to: '/mining',
      icon: <Mountain className="h-5 w-5" />,
      label: 'Minería & Energía',
      collapsed,
      children: [
        { to: '/mining', label: 'Dashboard Minería' },
        { to: '/mining/expediciones', label: 'Expediciones Mineras' },
        { to: '/mining/servicios', label: 'Servicios en Sitio' },
      ],
    },
    // RENTAL & ASSETS
    {
      to: '/rental',
      icon: <Boxes className="h-5 w-5" />,
      label: 'Rental & Assets',
      collapsed,
      module: 'rental',
      children: [
        { to: '/rental', label: 'Panel operativo' },
        { to: '/rental/assets', label: 'Activos e inventario' },
        { to: '/rental/customers', label: 'Clientes' },
        { to: '/rental/contracts', label: 'Alquileres' },
        { to: '/rental/pricing', label: 'Tarifas y garantías' },
        { to: '/rental/returns', label: 'Devoluciones' },
        { to: '/rental/operations', label: 'Inspección y service' },
        { to: '/rental/incidents', label: 'Daños e incidentes' },
        { to: '/rental/network', label: 'Red, inventario y caja' },
        { to: '/rental/reports', label: 'Reportes' },
      ],
    },
    // TRAZABILIDAD RFID
    {
      to: '/traceability',
      icon: <ScanLine className="h-5 w-5" />,
      label: 'Trazabilidad RFID',
      collapsed,
      children: [
        { to: '/traceability/commissioning', label: 'Comisionamiento R1' },
        { to: '/traceability/lab', label: 'Lab RFID' },
        { to: '/traceability/laundry', label: '🧺 Lavandería — Dashboard' },
        { to: '/traceability/laundry/prendas', label: '   · Prendas' },
        { to: '/traceability/laundry/recepcion', label: '   · Recepción' },
        { to: '/traceability/laundry/ordenes', label: '   · Órdenes de Lavado' },
        { to: '/traceability/laundry/despacho', label: '   · Despacho' },
        { to: '/traceability/laundry/recorridos', label: '   · Recorridos en Campo' },
        { to: '/traceability/laundry/clientes', label: '   · Clientes' },
        { to: '/traceability/laundry/incidentes', label: '   · Incidentes' },
        { to: '/traceability/laundry/reportes', label: '   · Reportes' },
        { to: '/traceability/agro', label: '🧄 Agroalimentario — Dashboard' },
        { to: '/traceability/agro/cosecha', label: '   · Cosecha' },
        { to: '/traceability/agro/clasificacion', label: '   · Clasificación' },
        { to: '/traceability/agro/empaque', label: '   · Empaque' },
        { to: '/traceability/agro/almacenamiento', label: '   · Almacenamiento' },
        { to: '/traceability/agro/despacho', label: '   · Despacho' },
      ],
    },
    // ─── LOGÍSTICA ────────────────────────────────────────────────
    { type: 'section', label: 'Logística', modules: ['freight', 'tracking', 'routes', 'weather', 'emissions', 'fleet', 'iot'] },
    // CENTRO DE CONTROL
    {
      to: '/tracking',
      icon: <MapPin className="h-5 w-5" />,
      label: 'Centro de Control',
      collapsed,
      permission: 'tracking:read',
      children: [
        { to: '/tracking/live', label: 'Monitor en Vivo' },
        { to: '/tracking/shipments', label: 'Control Tower (Cargas)' },
        { to: '/traffic/intelligence', label: 'Tráfico Inteligente' },
        { to: '/tracking/alerts', label: 'Panel de Alertas' },
        { to: '/tracking/geofences', label: 'Geocercas' },
      ],
    },
    // PLANIFICACIÓN Y RUTAS
    {
      to: '/routes',
      icon: <Route className="h-5 w-5" />,
      label: 'Gestión de Rutas',
      collapsed,
      permission: 'routes:read',
      children: [
        { to: '/routes/planner', label: 'Planificador Inteligente' },
        { to: '/routes', label: 'Mis Rutas' },
        { to: '/weather', label: 'Meteorología' },
        { to: '/emissions/calculator', label: 'Huella Carbono (GLEC)' },
        { to: '/routes/optimize', label: 'Optimización' },
        { to: '/routes/news', label: 'Noticias Viales' },
      ],
    },
    // FLOTA
    {
      to: '/fleet/dashboard',
      icon: <Truck className="h-5 w-5" />,
      label: 'Flota y Activos',
      collapsed,
      permission: 'fleet:read',
      children: [
        { to: '/fleet/dashboard', label: 'Dashboard Flota' },
        { to: '/fleet/agenda', label: 'Agenda Flota' },
        { to: '/fleet/vehicles', label: 'Vehículos' },
        { to: '/fleet/drivers', label: 'Conductores' },
        { to: '/fleet/maintenance', label: 'Mantenimiento' },
        { to: '/fleet/fuel', label: 'Combustible' },
        { to: '/fleet/documents', label: 'Documentación' },
        { to: '/fleet/inspection-photos', label: 'Inspecciones' },
        { to: '/fleet/transponders', label: 'Telepases' },
      ],
    },
    // ─── ORGANIZACIÓN ─────────────────────────────────────────────
    { type: 'section', label: 'Organización', modules: ['rrhh', 'projects'] },
    // RECURSOS HUMANOS
    {
      to: '/hr',
      icon: <Users className="h-5 w-5" />,
      label: 'People y RR. HH.',
      collapsed,
      children: [
        { to: '/people', label: 'Mi espacio' },
        { to: '/people/me', label: 'Mi perfil' },
        { to: '/people/community', label: 'Comunidad' },
        { to: '/people/notifications', label: 'Mis novedades' },
        { to: '/people/editorial', label: 'Centro editorial', permission: 'hr:communicate' },
        { to: '/people/documents', label: 'Documentos y procesos' },
        { to: '/people/journeys', label: 'Journeys y workflows', permission: 'hr:workflows' },
        { to: '/people/growth', label: 'Mi crecimiento' },
        { to: '/people/talent', label: 'Talento y sucesión', permission: 'hr:talent' },
        { to: '/people/operations', label: 'Mi día y servicios' },
        { to: '/people/operations/manage', label: 'People Operations', permission: 'hr:operations' },
        { to: '/people/benefits', label: 'Beneficios y recompensas' },
        { to: '/people/opportunities', label: 'Oportunidades internas' },
        { to: '/people/engagement/manage', label: 'Propuesta de valor', permission: 'hr:engagement' },
        { to: '/people/assistant', label: 'Asistente People AI' },
        { to: '/people/analytics', label: 'People Intelligence', permission: 'hr:analytics' },
        { to: '/people/integrations', label: 'SSO e integraciones', permission: 'hr:integrations' },
        { to: '/people/directory', label: 'Directorio' },
        { to: '/people/requests', label: 'Mis solicitudes' },
        { to: '/people/approvals', label: 'Aprobaciones', permission: 'hr:approve' },
        { to: '/hr/dashboard', label: 'Panel People' },
        { to: '/hr/agenda', label: 'Agenda' },
        { to: '/hr/employees', label: 'Personas' },
        { to: '/hr/attendance', label: 'Asistencia' },
        { to: '/hr/payroll', label: 'Nómina' },
        { to: '/hr/org-chart', label: 'Estructura' },
      ],
    },
    // PROYECTOS
    {
      to: '/projects',
      icon: <FolderKanban className="h-5 w-5" />,
      label: 'Proyectos',
      collapsed,
      children: [
        { to: '/projects/dashboard', label: 'Dashboard' },
        { to: '/projects/agenda', label: 'Agenda' },
        { to: '/projects', label: 'Mis Proyectos' },
        { to: '/projects/board', label: 'Tablero' },
      ],
    },
    // ─── FINANZAS ─────────────────────────────────────────────────
    { type: 'section', label: 'Finanzas', modules: ['accounting', 'fiscal', 'treasury', 'fixed_assets'] },
    // GESTIÓN DE ACTIVOS
    {
      to: '/fixed-assets',
      icon: <Boxes className="h-5 w-5" />,
      label: 'Gestión de Activos',
      permission: 'fixed_assets:read',
      module: 'fixed_assets',
      collapsed,
      children: [
        { to: '/fixed-assets/dashboard', label: 'Dashboard' },
        { to: '/fixed-assets/report', label: 'Reporte Anexo' },
        { to: '/fixed-assets/activaciones', label: 'Activaciones' },
        { to: '/fixed-assets', label: 'Inventario' },
      ],
    },
    // CONTABILIDAD
    {
      to: '/accounting',
      icon: <Calculator className="h-5 w-5" />,
      label: 'Contabilidad',
      collapsed,
      children: [
        { to: '/accounting/dashboard', label: 'Dashboard' },
        { to: '/accounting/agenda', label: 'Agenda' },
        { to: '/accounting/invoices', label: 'Facturas (CxC)' },
        { to: '/accounting/bills', label: 'Proveedores (CxP)' },
        { to: '/accounting/payments', label: 'Cobros y Pagos' },
        { to: '/accounting/assets', label: 'Activos Fijos' },
        { to: '/accounting/budget', label: 'Presupuesto' },
        { to: '/accounting/accounts', label: 'Plan de Cuentas' },
        { to: '/accounting/reports', label: 'Reportes' },
      ],
    },
    // FISCAL — ARCA & ADUANA
    {
      to: '/fiscal',
      icon: <Landmark className="h-5 w-5" />,
      label: 'Fiscal & Aduana',
      collapsed,
      children: [
        { to: '/fiscal', label: 'Panel Fiscal' },
        { to: '/fiscal/comprobantes', label: 'Comprobantes' },
        { to: '/fiscal/aduana', label: 'Operaciones Aduana' },
        { to: '/fiscal/libro-iva', label: 'Libro IVA Digital' },
        { to: '/fiscal/credenciales', label: 'Credenciales ARCA' },
        { to: '/fiscal/simulador', label: 'Simulador Tributos' },
      ],
    },
    // TESORERÍA
    {
      to: '/treasury',
      icon: <Wallet className="h-5 w-5" />,
      label: 'Tesorería',
      collapsed,
      children: [
        { to: '/treasury', label: 'Panel General' },
        { to: '/treasury/cuentas', label: 'Cuentas Bancarias' },
        { to: '/treasury/movimientos', label: 'Movimientos' },
        { to: '/treasury/cheques', label: 'Cheques' },
        { to: '/treasury/vencimientos', label: 'Vencimientos' },
        { to: '/treasury/cajas', label: 'Cajas Chicas' },
      ],
    },
    // ─── TECNOLOGÍA ───────────────────────────────────────────────
    { type: 'section', label: 'Tecnología', modules: ['drones', 'digital_sign', 'iot', 'risk'] },
    // IoT
    {
      to: '/devices',
      icon: <Cpu className="h-5 w-5" />,
      label: 'Tecnología IoT',
      collapsed,
      permission: 'devices:read',
      children: [
        { to: '/iot/live', label: '● IoT en Vivo (datos reales)' },
        { to: '/devices/provisioning', label: 'Provisionar Hardware' },
        { to: '/devices', label: 'Inventario Dispositivos' },
        { to: '/telemetry', label: 'Demo Telemetría (simulado)' },
        { to: '/maps/lorawan-demo', label: 'Demo LoRaWAN (Villavicencio)' },
        { to: '/integrations', label: 'Integraciones' },
      ],
    },
    // DRONES & RUTAS AÉREAS
    {
      to: '/drones',
      icon: <Plane className="h-5 w-5" />,
      label: 'Drones & Rutas',
      collapsed,
      children: [
        { to: '/drones', label: 'Panel General' },
        { to: '/drones/flota', label: 'Flota Drones' },
        { to: '/drones/vuelos', label: 'Vuelos' },
        { to: '/drones/rutas', label: 'Rutas Aéreas' },
        { to: '/drones/archivos', label: 'Archivos & Media' },
        { to: '/drones/vision-ia', label: 'Visión IA' },
        { to: '/drones/marcas', label: 'Marcas & Formatos' },
      ],
    },
    // FIRMA DIGITAL
    {
      to: '/documents/signing',
      icon: <FilePen className="h-5 w-5" />,
      label: 'Firma Digital',
      permission: 'hr:documents',
      collapsed,
    },
    // WORLD MONITOR
    {
      to: '/risk',
      icon: <ShieldAlert className="h-5 w-5" />,
      label: 'World Monitor',
      collapsed,
      children: [
        { to: '/risk/dashboard', label: 'Monitor de Riesgos' },
      ],
    },
    // ─── BUSINESS INTELLIGENCE ────────────────────────────────────
    { type: 'section', label: 'Business Intelligence', modules: ['reports', 'ai'] },
    // ANALÍTICA & REPORTES
    {
      to: '/reports',
      icon: <FileText className="h-5 w-5" />,
      label: 'Analítica & Reportes',
      collapsed,
      permission: 'reports:read',
      children: [
        { to: '/reports', label: 'Reportes y Estadísticas' },
        { to: '/ai', label: 'Asistente IA' },
        { to: '/maps/mendoza', label: 'Cartografía Oficial (IGN)' },
        { to: '/web-analytics', label: 'Redes & Web' },
      ],
    },
  ]

  // Filter nav items by active logistics mode (admin switcher)
  const modeFilteredItems = user?.is_superuser
    ? filterSuperuserNavigation(allNavItems, activeMode, selectedDemoSections)
    : allNavItems.filter(
        (item) => 'type' in item || isSectionVisibleForMode(item.to, effectiveMode)
      )
  const navItems = modeFilteredItems.filter((item, index, items) => {
    if (!('type' in item)) return true
    for (let nextIndex = index + 1; nextIndex < items.length; nextIndex += 1) {
      if ('type' in items[nextIndex]) return false
      return true
    }
    return false
  })

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-screen bg-card border-r transition-all duration-300",
          open ? "w-64" : "w-20",
          "lg:translate-x-0",
          !open && "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'relative flex items-center border-b transition-[height] duration-300',
            collapsed
              ? 'h-16 justify-center px-4'
              : 'h-28 justify-between overflow-hidden bg-black'
          )}
        >
          {collapsed ? (
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary">
              <img src="/favicon.svg" alt="ARAM" className="h-5 w-5" />
            </div>
          ) : (
            <video
              className="absolute inset-0 h-full w-full object-cover object-top"
              src="/media/aram-intro.mp4"
              aria-label="Animación de ARAM"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
            />
          )}
          
          <button
            className="absolute right-3 z-10 rounded-md bg-black/40 p-1 text-white backdrop-blur-sm hover:bg-black/60 lg:hidden"
            onClick={onClose}
            aria-label="Cerrar navegación"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav
          className={cn(
            'space-y-1 overflow-y-auto p-4 transition-[height] duration-300',
            collapsed ? 'h-[calc(100vh-4rem)]' : 'h-[calc(100vh-7rem)]'
          )}
          onClick={(e) => {
            // Auto-close sidebar when navigating on mobile/tablet
            if (window.innerWidth < 1024 && (e.target as HTMLElement).closest('a')) {
              onClose()
            }
          }}
        >
          {navItems.map((item, idx) => {
            if ('type' in item) {
              const enabledModules = user?.enabled_modules ?? []
              const menuPerms = user?.menu_permissions

              // When menu_permissions is set, hide section if none of its child routes are allowed
              if (item.modules && menuPerms !== null && menuPerms !== undefined && !menuPerms.includes('ALL')) {
                // Gather routes in this section by scanning ahead in allNavItems
                const sectionStart = allNavItems.indexOf(item)
                const nextSectionIdx = allNavItems.findIndex(
                  (n, i) => i > sectionStart && 'type' in n
                )
                const sectionItems = allNavItems.slice(
                  sectionStart + 1,
                  nextSectionIdx === -1 ? undefined : nextSectionIdx
                )
                const anyVisible = sectionItems.some(n => {
                  if ('type' in n) return false
                  const routes = n.children ? n.children.flatMap(c => [c.to, ...(c.legacyPaths ?? [])]) : [n.to]
                  return routes.some(r => menuPerms.some(p => r === p || r.startsWith(p + '/')))
                })
                if (!anyVisible) return null
              }

              // Hide section header if user has restricted modules and none apply to this section
              if (
                item.modules &&
                enabledModules.length > 0 &&
                !enabledModules.includes('_full') &&
                !item.modules.some(m => enabledModules.includes(m)) &&
                (menuPerms === null || menuPerms === undefined)
              ) {
                return null
              }
              return <SectionHeader key={`section-${idx}`} label={item.label} collapsed={collapsed} />
            }
            const modeChildren = item.children
              ? item.children.filter((child) => isLogisticsChildVisibleForMode(child.to, effectiveMode))
              : item.children
            return <NavItem key={item.to} {...item} children={modeChildren} />
          })}
        </nav>
      </aside>
    </>
  )
}
